/**
 * The right column of both modes: "Đã render" (ID photos and edited photos of the last 24 h in one list; an item
 * opens in the mode it belongs to), "Token" (wallets, what a render costs in this mode, ads) and the fine print.
 */
import { useState, type ReactNode, type Ref } from "react";
import { adsEnabled } from "../ads/enabled";
import type { Api, Job } from "../api";
import { AdOffer } from "../components/AdOffer";
import { config } from "../config";
import { EDIT, LEGAL, STATUS_LABELS, tierLine } from "../messages";
import { isEditJob, isSharpenJob, isVariantsJob } from "./jobs";
import { SPEC_GUIDE_PATH } from "./specLink";
import type { Studio } from "./types";

interface Props {
  studio: Studio;
  /** the jobs the mode shows now (highlighted in the list) */
  activeIds: (string | null)[];
  /** what a render costs in this mode, under the wallets */
  hint: ReactNode;
  emphasize?: boolean;
  coinsRef?: Ref<HTMLElement>;
  /** tokens were added (ad watched) */
  onCoins?: () => void;
}

function Thumb({ api, job }: { api: Api; job: Job }) {
  const [broken, setBroken] = useState(false);
  if (job.status !== "done" || !job.files.photo || broken) {
    return <span className={`thumb thumb-${job.status}`} aria-hidden="true">{job.status === "done" ? "✓" : "…"}</span>;
  }
  // signed link: valid 15 minutes, refreshed with the list
  return <img className="thumb" src={api.fileUrl(job.files.photo)} alt="" loading="lazy" onError={() => setBroken(true)} />;
}

export function Side({ studio, activeIds, hint, emphasize = false, coinsRef, onCoins }: Props) {
  const { api, me, auth, jobs } = studio;
  const signedIn = auth === "in";
  const sharpenId = studio.sharpen.job?.id ?? null;
  const shown = new Set([...activeIds, sharpenId].filter(Boolean));
  const login = () => studio.requestLogin({ forRender: false });

  function labelOf(j: Job): string {
    if (isSharpenJob(j)) return EDIT.sharpen.historyLabel;
    if (isVariantsJob(j)) return EDIT.quick.historyLabel;
    if (isEditJob(j)) return EDIT.nav.edit;
    const spec = j.settings.spec;
    return studio.catalog?.specs.find((s) => s.key === spec)?.label ?? spec;
  }

  return (
    <div className="side">
      <section className="card history" aria-labelledby="history-title">
        <h2 id="history-title">Đã render{signedIn ? ` (${jobs.length})` : ""}</h2>
        {!signedIn ? (
          <>
            <p className="hint">Đăng nhập để xem lại và tải các ảnh đã render trong 24 giờ qua.</p>
            <button type="button" className="btn btn-small" onClick={login}>Đăng nhập</button>
          </>
        ) : jobs.length === 0 ? (
          <p className="hint">Chưa có ảnh nào trong 24 giờ qua.</p>
        ) : (
          <ul>
            {jobs.map((j) => (
              <li key={j.id}>
                <button type="button" className={`history-item${shown.has(j.id) ? " is-active" : ""}`}
                        onClick={() => studio.openJob(j)} aria-current={shown.has(j.id) || undefined}>
                  <Thumb api={api} job={j} />
                  <span className="history-text">
                    <span>
                      {new Date(j.created_at).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}
                      {" · "}<span className={`status status-${j.status}`}>{STATUS_LABELS[j.status]}</span>
                    </span>
                    <span className="muted">{labelOf(j)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section ref={coinsRef} className={`card coins${emphasize ? " is-emphasized" : ""}`} aria-labelledby="coins-title">
        <h2 id="coins-title">Token</h2>
        {signedIn && me && (
          <dl className="wallets">
            <div><dt>Free hôm nay</dt><dd>{me.daily}</dd></div>
            {(adsEnabled() || me.earned > 0) && <div><dt>Quảng cáo</dt><dd>{me.earned}</dd></div>}
            <div><dt>Nạp</dt><dd>{me.topup ?? 0}</dd></div>
          </dl>
        )}
        {tierLine(me?.tier, me?.tier_until) && <p className="tier-line">{tierLine(me?.tier, me?.tier_until)}</p>}
        <p className="hint">{hint}</p>
        <AdOffer api={api} me={me} guest={!signedIn} onNeedLogin={login}
                 onCoins={() => { onCoins?.(); void studio.refreshMe(); }} emphasize={emphasize} />
      </section>
      <footer className="fineprint">
        <p>Ảnh được xử lý trên máy chủ của DTAA, tự xoá sau 24 giờ, không dùng để huấn luyện AI.</p>
        <p>
          <a className="link" href={LEGAL.privacy} target="_blank" rel="noopener">Chính sách quyền riêng tư</a>
          {" · "}
          <a className="link" href={LEGAL.terms} target="_blank" rel="noopener">Điều khoản sử dụng</a>
          {" · "}
          <a className="link" href={SPEC_GUIDE_PATH} target="_blank" rel="noopener">Hướng dẫn quy cách</a>
        </p>
        {config.authMode === "dev" && (
          <p>Chế độ dev · {signedIn ? `người dùng thử: ${studio.devName ?? "web-dev"}` : "chưa đăng nhập"}</p>
        )}
      </footer>
    </div>
  );
}
